import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import EventImagePicker from "../CreateEvent/EventImagePicker/EventImagePicker";
import { LEGACY_IMAGE_OPTIONS } from "../CreateEvent/legacyImageOptions";
import { searchLocations } from "../../../api/eventLocations";
import "./EditEventsTab.css";

// GraphQL — defined inline so this component owns its data shape contract.
const EDITABLE_EVENTS = gql`
  query EditableSocialEvents(
    $sinceDate: String
    $beforeDate: String
    $limit: Int
  ) {
    editableSocialEvents(
      sinceDate: $sinceDate
      beforeDate: $beforeDate
      limit: $limit
    ) {
      id
      title
      date
      time
      description
      generalpublic
      location
      privateLocation
      imgKey
      length
      groupmeImg
      password
    }
  }
`;

const UPDATE_SOCIAL_EVENT = gql`
  mutation UpdateSocialEvent(
    $socialEventId: ID!
    $input: SocialEventUpdateInput!
  ) {
    updateSocialEvent(socialEventId: $socialEventId, input: $input) {
      id
      title
      date
      time
      description
      generalpublic
      location
      privateLocation
      imgKey
      length
      groupmeImg
      password
    }
  }
`;

const DELETE_SOCIAL_EVENT = gql`
  mutation DeleteSocialEvent($socialEventId: ID!) {
    deleteSocialEvent(socialEventId: $socialEventId)
  }
`;

// ---- date helpers ----
// Backend defaults sinceDate to (today − 2 calendar days) in UTC. We mirror
// that on the client by computing UTC YYYY-MM-DD so the URL/state matches what
// the server would have chosen if we sent nothing. Using UTC also avoids
// off-by-one issues for users west of UTC at late hours.
const todayUtcYmd = () => {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  const d = String(now.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const ymdMinusDays = (ymd, days) => {
  // Parse YYYY-MM-DD as a UTC midnight, subtract days, format back as UTC YYYY-MM-DD.
  const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
  const ms = Date.UTC(y, m - 1, d) - days * 24 * 60 * 60 * 1000;
  const dt = new Date(ms);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

// Convert a stored event date (could be "Month DD, YYYY" or YYYY-MM-DD or ISO)
// into "YYYY-MM-DD" for the date input. Best-effort: returns "" on failure.
const toDateInputValue = (raw) => {
  if (!raw) return "";
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Convert a stored event time (e.g. "6:00 PM" or "18:00") to "HH:MM" for the
// time input. Returns "" on failure.
const toTimeInputValue = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  // Already HH:MM or HH:MM:SS
  const m24 = s.match(/^(\d{1,2}):(\d{2})/);
  if (m24 && !/AM|PM/i.test(s)) {
    const h = String(parseInt(m24[1], 10)).padStart(2, "0");
    return `${h}:${m24[2]}`;
  }
  // 12-hour with AM/PM
  const m12 = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (m12) {
    let h = parseInt(m12[1], 10) % 12;
    if (/PM/i.test(m12[3])) h += 12;
    return `${String(h).padStart(2, "0")}:${m12[2]}`;
  }
  return "";
};

// Build a stable "edit draft" from a server event row.
const draftFromEvent = (ev) => ({
  title: ev.title || "",
  date: toDateInputValue(ev.date),
  time: toTimeInputValue(ev.time),
  description: ev.description || "",
  generalpublic: !!ev.generalpublic,
  location: ev.location || "",
  privateLocation: ev.privateLocation || "",
  imgKey: ev.imgKey || "",
  length: ev.length != null ? String(ev.length) : "",
  groupmeImg: ev.groupmeImg || "",
  password: ev.password != null ? String(ev.password) : "",
});

// Build the same shape from the original row so we can diff before sending —
// only changed fields are sent to the mutation.
const originalShapeFromEvent = (ev) => ({
  title: ev.title || "",
  date: toDateInputValue(ev.date),
  time: toTimeInputValue(ev.time),
  description: ev.description || "",
  generalpublic: !!ev.generalpublic,
  location: ev.location || "",
  privateLocation: ev.privateLocation || "",
  imgKey: ev.imgKey || "",
  length: ev.length != null ? String(ev.length) : "",
  groupmeImg: ev.groupmeImg || "",
  password: ev.password != null ? String(ev.password) : "",
});

// --- Location autocomplete subcomponent ---
// Owns its own debounce + dropdown state so the parent's edit form stays
// declarative. Calls onChange(value) any time the admin types or picks; the
// value sent is the freeform "Title, Address" string the rest of the app
// expects (mirrors CreateEvent.js's hardcoded list shape).
const LocationAutocomplete = ({ value, onChange, disabled }) => {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Sync external value changes (e.g. when starting edit on a different event).
  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  // Debounced fetch when the box is focused/open.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const q = (query || "").trim();
    const handle = setTimeout(() => {
      searchLocations(q)
        .then((resp) => {
          if (cancelled) return;
          setResults(Array.isArray(resp && resp.results) ? resp.results : []);
        })
        .catch((err) => {
          if (cancelled) return;
          // eslint-disable-next-line no-console
          console.warn("Location search failed:", err);
          setResults([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, open]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handlePick = (r) => {
    const composed = `${r.title || ""}${r.address ? ", " + r.address : ""}`;
    setQuery(composed);
    onChange(composed);
    setOpen(false);
  };

  const sortedResults = useMemo(
    () =>
      [...(results || [])].sort(
        (a, b) => (b?.favorite ? 1 : 0) - (a?.favorite ? 1 : 0)
      ),
    [results]
  );

  return (
    <div className="edit-events-tab__autocomplete" ref={containerRef}>
      <input
        type="text"
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Type to search locations…"
      />
      {open && sortedResults.length > 0 && (
        <ul className="edit-events-tab__autocomplete-list">
          {sortedResults.map((r, idx) => (
            <li key={`${r.title}-${idx}`} onClick={() => handlePick(r)}>
              {r.favorite ? (
                <span
                  className="edit-events-tab__star"
                  aria-label="Favorite"
                  title="Favorite"
                >
                  ★
                </span>
              ) : null}
              {r.title}
              {r.address ? (
                <span style={{ color: "#6b7280", marginLeft: 4 }}>
                  — {r.address}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {open && sortedResults.length === 0 && (query || "").trim().length > 0 && (
        <ul className="edit-events-tab__autocomplete-list">
          <li className="edit-events-tab__autocomplete-empty">
            No matches — keep typing to use as freeform value
          </li>
        </ul>
      )}
    </div>
  );
};

const EditEventsTab = () => {
  const [sinceDate, setSinceDate] = useState(() =>
    ymdMinusDays(todayUtcYmd(), 2)
  );

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [rowError, setRowError] = useState({});
  const [toast, setToast] = useState(null);

  const showToast = useCallback((kind, message) => {
    setToast({ kind, message });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  const { data, loading, error, refetch } = useQuery(EDITABLE_EVENTS, {
    variables: { sinceDate, limit: 200 },
    fetchPolicy: "cache-and-network",
  });

  const [updateSocialEvent] = useMutation(UPDATE_SOCIAL_EVENT);
  const [deleteSocialEvent] = useMutation(DELETE_SOCIAL_EVENT);

  // Typed-confirmation modal state for destructive deletes.
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const requestDelete = (ev) => {
    setConfirmingDeleteId(ev.id);
    setConfirmText("");
  };

  const cancelDelete = () => {
    setConfirmingDeleteId(null);
    setConfirmText("");
  };

  const performDelete = async (ev) => {
    const id = ev.id;
    setDeletingId(id);
    try {
      await deleteSocialEvent({ variables: { socialEventId: id } });
      // Refetch the list so the deleted row disappears.
      await refetch();
      showToast("success", `Deleted "${ev.title || id}"`);
      cancelDelete();
    } catch (err) {
      const msg =
        (err && err.graphQLErrors && err.graphQLErrors[0] && err.graphQLErrors[0].message) ||
        (err && err.message) ||
        "Delete failed";
      showToast("error", msg);
    } finally {
      setDeletingId(null);
    }
  };

  // The query is the source of truth for the list. Local state only tracks
  // edit-in-progress; once the mutation returns, Apollo's normalized cache
  // updates the row in `data` and the table re-renders.
  const events = useMemo(() => {
    const list = (data && data.editableSocialEvents) || [];
    // Server promises desc by socialEventId, but defensively re-sort.
    return [...list].sort((a, b) =>
      String(b.id || "").localeCompare(
        String(a.id || "")
      )
    );
  }, [data]);

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setEditDraft(draftFromEvent(ev));
    setRowError((prev) => {
      const next = { ...prev };
      delete next[ev.id];
      return next;
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const updateDraftField = (field, value) => {
    setEditDraft((d) => ({ ...d, [field]: value }));
  };

  const saveEdit = async (ev) => {
    if (!editDraft) return;
    const id = ev.id;
    const original = originalShapeFromEvent(ev);

    // Diff: only send fields that actually changed.
    const input = {};
    Object.keys(editDraft).forEach((key) => {
      const a = editDraft[key];
      const b = original[key];
      // Normalize booleans + strings for comparison.
      if (typeof a === "boolean" || typeof b === "boolean") {
        if (!!a !== !!b) input[key] = !!a;
      } else if ((a || "") !== (b || "")) {
        input[key] = a;
      }
    });

    if (Object.keys(input).length === 0) {
      cancelEdit();
      showToast("info", "No changes to save.");
      return;
    }

    setSavingId(id);
    try {
      const resp = await updateSocialEvent({
        variables: { socialEventId: id, input },
        // Apollo merges the returned SocialEvent (matched by id) back into the
        // EDITABLE_EVENTS result automatically thanks to normalized caching.
      });
      if (resp && resp.data && resp.data.updateSocialEvent) {
        showToast("success", "Event updated.");
        cancelEdit();
      } else {
        throw new Error("Mutation returned no data");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("updateSocialEvent failed:", err);
      const msg = (err && err.message) || "Update failed.";
      setRowError((prev) => ({ ...prev, [id]: msg }));
      showToast("error", msg);
    } finally {
      setSavingId(null);
    }
  };

  const loadOlder = () => {
    // Strategy B from the spec: just widen the window. Apollo refetches with
    // the new variables and returns everything in the wider range, so the
    // already-loaded items stay visible.
    setSinceDate((prev) => ymdMinusDays(prev, 7));
  };

  return (
    <div className="edit-events-tab">
      <div className="edit-events-tab__header">
        <h2>Edit Events</h2>
        <div className="edit-events-tab__filter">
          <label htmlFor="edit-events-since">Since:</label>
          <input
            id="edit-events-since"
            type="date"
            value={sinceDate}
            onChange={(e) => setSinceDate(e.target.value)}
          />
          <span className="edit-events-tab__count">
            ({events.length} event{events.length === 1 ? "" : "s"})
          </span>
          <button
            type="button"
            className="edit-events-tab__btn"
            onClick={() => refetch()}
            disabled={loading}
          >
            Refresh
          </button>
        </div>
      </div>

      {toast && (
        <div
          className={`edit-events-tab__status edit-events-tab__status--${toast.kind === "info" ? "info" : toast.kind}`}
        >
          {toast.message}
        </div>
      )}

      {loading && events.length === 0 && (
        <div className="edit-events-tab__status edit-events-tab__status--info">
          Loading events…
        </div>
      )}

      {error && (
        <div className="edit-events-tab__status edit-events-tab__status--error">
          Could not load events: {error.message || "unknown error"}
        </div>
      )}

      {!loading && !error && events.length === 0 && (
        <div className="edit-events-tab__status edit-events-tab__status--info">
          No editable events in this window.
        </div>
      )}

      {events.length > 0 && (
        <div className="edit-events-tab__table-wrap">
          <table className="edit-events-tab__table">
            <thead>
              <tr>
                <th>Date / Time</th>
                <th>Title</th>
                <th>Location</th>
                <th>ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => {
                const id = ev.id;
                const isEditing = editingId === id;
                const isSaving = savingId === id;
                const errMsg = rowError[id];
                return (
                  <React.Fragment key={id}>
                    <tr
                      className={`edit-events-tab__row${
                        isEditing ? " edit-events-tab__row--editing" : ""
                      }`}
                    >
                      <td className="edit-events-tab__date-col">
                        {ev.date || "—"}
                        <br />
                        <span style={{ color: "#6b7280" }}>
                          {ev.time || ""}
                        </span>
                      </td>
                      <td className="edit-events-tab__title-col">
                        {ev.title || "(untitled)"}
                      </td>
                      <td className="edit-events-tab__location-col" title={ev.location}>
                        {ev.location || "—"}
                      </td>
                      <td className="edit-events-tab__id-col">{id}</td>
                      <td>
                        {isEditing ? (
                          <button
                            type="button"
                            className="edit-events-tab__btn"
                            onClick={cancelEdit}
                            disabled={isSaving}
                          >
                            Close
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="edit-events-tab__btn"
                              onClick={() => startEdit(ev)}
                            >
                              Edit
                            </button>{" "}
                            <button
                              type="button"
                              className="edit-events-tab__btn edit-events-tab__btn--danger"
                              onClick={() => requestDelete(ev)}
                              disabled={deletingId === id}
                            >
                              {deletingId === id ? "Deleting…" : "Delete"}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    {isEditing && editDraft && (
                      <tr className="edit-events-tab__row--editing">
                        <td colSpan={5} style={{ padding: 0 }}>
                          <div className="edit-events-tab__edit-card">
                            <div className="edit-events-tab__field edit-events-tab__field--full">
                              <label>Title</label>
                              <input
                                type="text"
                                value={editDraft.title}
                                disabled={isSaving}
                                onChange={(e) =>
                                  updateDraftField("title", e.target.value)
                                }
                              />
                            </div>

                            <div className="edit-events-tab__field">
                              <label>Date</label>
                              <input
                                type="date"
                                value={editDraft.date}
                                disabled={isSaving}
                                onChange={(e) =>
                                  updateDraftField("date", e.target.value)
                                }
                              />
                            </div>

                            <div className="edit-events-tab__field">
                              <label>Time</label>
                              <input
                                type="time"
                                value={editDraft.time}
                                disabled={isSaving}
                                onChange={(e) =>
                                  updateDraftField("time", e.target.value)
                                }
                              />
                            </div>

                            <div className="edit-events-tab__field edit-events-tab__field--full">
                              <label>Description</label>
                              <textarea
                                value={editDraft.description}
                                disabled={isSaving}
                                onChange={(e) =>
                                  updateDraftField(
                                    "description",
                                    e.target.value
                                  )
                                }
                              />
                            </div>

                            <div className="edit-events-tab__field edit-events-tab__field--full edit-events-tab__checkbox-row">
                              <input
                                id={`gp-${id}`}
                                type="checkbox"
                                checked={editDraft.generalpublic}
                                disabled={isSaving}
                                onChange={(e) =>
                                  updateDraftField(
                                    "generalpublic",
                                    e.target.checked
                                  )
                                }
                              />
                              <label htmlFor={`gp-${id}`}>
                                General Public (visible to everyone)
                              </label>
                            </div>

                            <div className="edit-events-tab__field edit-events-tab__field--full">
                              <label>Location</label>
                              <LocationAutocomplete
                                value={editDraft.location}
                                disabled={isSaving}
                                onChange={(v) =>
                                  updateDraftField("location", v)
                                }
                              />
                            </div>

                            <div className="edit-events-tab__field edit-events-tab__field--full">
                              <label>Private Location</label>
                              <input
                                type="text"
                                value={editDraft.privateLocation}
                                disabled={isSaving}
                                onChange={(e) =>
                                  updateDraftField(
                                    "privateLocation",
                                    e.target.value
                                  )
                                }
                              />
                            </div>

                            <div className="edit-events-tab__field">
                              <label>Length (hours)</label>
                              <input
                                type="text"
                                value={editDraft.length}
                                disabled={isSaving}
                                onChange={(e) =>
                                  updateDraftField("length", e.target.value)
                                }
                              />
                            </div>

                            <div className="edit-events-tab__field">
                              <label>Host ID Number (admin only)</label>
                              <input
                                type="text"
                                value={editDraft.password}
                                disabled={isSaving}
                                onChange={(e) =>
                                  updateDraftField("password", e.target.value)
                                }
                              />
                            </div>

                            <div className="edit-events-tab__field edit-events-tab__field--full">
                              <label>Event Image</label>
                              <EventImagePicker
                                value={editDraft.imgKey}
                                onChange={(v) =>
                                  updateDraftField("imgKey", v)
                                }
                                legacyOptions={LEGACY_IMAGE_OPTIONS}
                              />
                            </div>

                            <div className="edit-events-tab__field edit-events-tab__field--full">
                              <label>GroupMe Image URL</label>
                              <input
                                type="text"
                                value={editDraft.groupmeImg}
                                disabled={isSaving}
                                onChange={(e) =>
                                  updateDraftField(
                                    "groupmeImg",
                                    e.target.value
                                  )
                                }
                                placeholder="https://i.groupme.com/..."
                              />
                            </div>

                            {errMsg && (
                              <div
                                className="edit-events-tab__inline-error edit-events-tab__field--full"
                              >
                                {errMsg}
                              </div>
                            )}

                            <div className="edit-events-tab__edit-actions">
                              <button
                                type="button"
                                className="edit-events-tab__btn"
                                onClick={cancelEdit}
                                disabled={isSaving}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="edit-events-tab__btn edit-events-tab__btn--primary"
                                onClick={() => saveEdit(ev)}
                                disabled={isSaving}
                              >
                                {isSaving ? "Saving…" : "Save"}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!error && (
        <div className="edit-events-tab__load-older">
          <button
            type="button"
            className="edit-events-tab__btn edit-events-tab__btn--ghost"
            onClick={loadOlder}
            disabled={loading}
          >
            Load older events (+7 days)
          </button>
        </div>
      )}

      {confirmingDeleteId && (() => {
        const ev = (events || []).find((e) => e.id === confirmingDeleteId);
        if (!ev) return null;
        const ready = confirmText.trim().toLowerCase() === "delete";
        const busy = deletingId === confirmingDeleteId;
        return (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={busy ? undefined : cancelDelete}
          >
            <div
              style={{
                background: "#fff",
                color: "#212529",
                padding: "20px",
                borderRadius: "8px",
                maxWidth: "440px",
                width: "92%",
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ marginTop: 0, color: "#dc3545" }}>Delete this event?</h3>
              <p style={{ margin: "8px 0" }}>
                <strong>{ev.title || ev.id}</strong>
              </p>
              <p style={{ margin: "8px 0", fontSize: "13px", color: "#6c757d" }}>
                This permanently removes the row from socialEvents. This cannot be undone.
                Type <strong>delete</strong> to confirm.
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type 'delete' to confirm"
                disabled={busy}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  marginTop: "8px",
                  boxSizing: "border-box",
                }}
                autoFocus
              />
              <div style={{ marginTop: "16px", textAlign: "right" }}>
                <button
                  type="button"
                  className="edit-events-tab__btn"
                  onClick={cancelDelete}
                  disabled={busy}
                >
                  Cancel
                </button>{" "}
                <button
                  type="button"
                  className="edit-events-tab__btn edit-events-tab__btn--danger"
                  onClick={() => performDelete(ev)}
                  disabled={!ready || busy}
                >
                  {busy ? "Deleting…" : "Delete event"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default EditEventsTab;
